import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  canDecreaseLayoutEditorColSpan,
  canIncreaseLayoutEditorColSpan,
  computeLayoutEditorAutoWidthUpdates,
  getLayoutEditorColSpanClass,
  getLayoutEditorColSpanLabel,
  normalizeLayoutEditorColSpan,
  LAYOUT_EDITOR_OVERLAY_Z_CLASS,
} from './dashboard-layout-editor.helpers';
import { LayoutEditorOverlayDirective } from './layout-editor-overlay.directive';
import { IconComponentV2 } from '../icon-component/icon.component';

export interface DashboardLayoutEditorItem {
  id: string;
  title: string;
  visible: boolean;
  colSpan: number;
  order: number;
  /**
   * Designed default col-span for this card. When set, the "decrease width"
   * stepper refuses to shrink the card below this value (e.g. a card
   * designed for `col-8` can only step between 8 and 12). When omitted, the
   * stepper is unrestricted.
   */
  defaultColSpan?: number | null;
}

@Component({
  selector: 'app-dashboard-layout-editor',
  standalone: true,
  imports: [CommonModule, DragDropModule, LayoutEditorOverlayDirective, IconComponentV2],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './dashboard-layout-editor.component.html',
})
export class DashboardLayoutEditorComponent {
  readonly overlayZClass = LAYOUT_EDITOR_OVERLAY_Z_CLASS;

  readonly open = input.required<boolean>();
  readonly items = input.required<DashboardLayoutEditorItem[]>();
  readonly visibleCount = input.required<number>();
  /** Optional: tiles that span two rows in the 12-col preview grid. */
  readonly isTallTile = input<(item: DashboardLayoutEditorItem) => boolean>();

  readonly closed = output<void>();
  readonly applied = output<void>();
  readonly resetDefaults = output<void>();
  readonly cardDropped = output<CdkDragDrop<DashboardLayoutEditorItem[]>>();
  readonly visibilityToggled = output<string>();
  readonly colSpanDecreased = output<string>();
  readonly colSpanIncreased = output<string>();
  /**
   * Emitted when the user clicks "Auto Adjust Width". The parent should call
   * {@link computeLayoutEditorAutoWidthUpdates} on its draft items and apply
   * the resulting `id → new colSpan` updates to its editor draft.
   */
  readonly autoAdjustWidth = output<void>();

  trackItem = (_idx: number, item: DashboardLayoutEditorItem): string => item.id;

  colSpanClass = getLayoutEditorColSpanClass;
  colSpanLabel = getLayoutEditorColSpanLabel;
  normalizeLayoutEditorColSpan = normalizeLayoutEditorColSpan;
  canDecrease = canDecreaseLayoutEditorColSpan;
  canIncrease = canIncreaseLayoutEditorColSpan;

  /**
   * Reactive guard for the Auto Adjust Width button. Recomputes whenever
   * `items` or `isTallTile` change so the button only enables when clicking
   * it would actually grow at least one card.
   */
  readonly canAutoAdjust = computed<boolean>(() => {
    const list = this.items();
    if (!list || !list.length) return false;
    const tallFn = this.isTallTile();
    const updates = computeLayoutEditorAutoWidthUpdates(
      list,
      tallFn ? (i) => tallFn(i) : undefined,
    );
    return updates.size > 0;
  });

  isTall(item: DashboardLayoutEditorItem): boolean {
    const fn = this.isTallTile();
    return fn ? fn(item) : false;
  }

  onBackdropClick(): void {
    this.closed.emit();
  }

  onDrop(event: CdkDragDrop<DashboardLayoutEditorItem[]>): void {
    this.cardDropped.emit(event);
  }

  onAutoAdjustClick(): void {
    if (!this.canAutoAdjust()) return;
    this.autoAdjustWidth.emit();
  }
}
